import React, { useCallback, useMemo } from "react";
import { AHWidgetTitle, Box, useTheme, useTime, useWidgetOptions } from "arkh";
import { BaseWidgetProps } from "../BaseProps";

import { graphql, useFragment } from "react-relay";

import {
  WidgetOverviewSize,
  WidgetTeamSchedule_widget$key,
} from "./__generated__/WidgetTeamSchedule_widget.graphql";
import { WidgetTeamScheduleContentSummary } from "./WidgetTeamScheduleContentSummary";
import { WidgetTeamScheduleContentDaily } from "./WidgetTeamScheduleContentDaily";
import { WidgetTeamScheduleContentWeekly } from "./WidgetTeamScheduleContentWeekly";
import { WidgetTeamScheduleContentMonthly } from "./WidgetTeamScheduleContentMonthly";
import { Pressable } from "react-native";
import Color from "color";
import { useSetWidgetFocus } from "../useSetFocus";
import { WidgetTeamScheduleContentBaseProps } from "./WidgetTeamScheduleContentBaseProps";
import { DateTime } from "luxon";
import { useSetScrollFocusMutation } from "../Focus/useSetFocusScroll";
import { useEvent } from "../useEvent";

export const WidgetTeamSchedule = React.memo(WidgetTeamScheduleCmp);

function WidgetTeamScheduleCmp(
  props: {
    widget: WidgetTeamSchedule_widget$key;
    forceSize?: WidgetOverviewSize;
  } & BaseWidgetProps & { now?: DateTime | undefined }
) {
  const widget = useFragment(
    graphql`
      fragment WidgetTeamSchedule_widget on WidgetTeamSchedule {
        id
        overviewSize
        title
        teams {
          id
          color
        }
        content {
          __typename
          ... on WidgetTeamScheduleContentDaily {
            ...WidgetTeamScheduleContentDaily_WidgetTeamScheduleContentDaily
          }
          ... on WidgetTeamScheduleContentWeekly {
            ...WidgetTeamScheduleContentWeekly_WidgetTeamScheduleContentWeekly
          }
          ... on WidgetTeamScheduleContentMonthly {
            ...WidgetTeamScheduleContentMonthly_WidgetTeamScheduleContentMonthly
          }
          ... on WidgetTeamScheduleContentSummary {
            ...WidgetTeamScheduleContentSummary_WidgetTeamScheduleContentSummary
          }
        }
      }
    `,
    props.widget
  );

  const theme = useTheme();

  const tint = useMemo(
    () =>
      widget.teams.length === 1
        ? (widget.teams[0].color ?? theme.primary)
        : theme.primary,
    [theme.primary, widget.teams]
  );

  const isDarkTint = Color(tint).darken(0.35).isDark();
  const _now = useTime(60000);
  const now = props.now ?? _now;

  const setScroll = useSetScrollFocusMutation(props.storecastId, widget.id);

  const onPressRoutineOccasion = useEvent(
    (routineId: string, sequenceNumber: number) => {
      if (props.mode === "FOCUS" && props.highlightedItemId !== routineId) {
        setFocus(routineId);
      } else {
        props.onEvent?.({
          kind: "RoutineOccasion",
          widgetId: widget.id,
          routineId,
          sequenceNumber,
        });
      }
    }
  );
  const size = props.forceSize ?? widget.overviewSize;

  const renderContent = useCallback(() => {
    const teamId = widget.teams[0]?.id;
    if (!widget.content || !teamId) {
      return null;
    }
    const baseProps: WidgetTeamScheduleContentBaseProps = {
      isDarkTint,
      tint,
      widgetId: widget.id,
      interactive: props.interactive,
      size,
      mode: props.mode,
      onPressRoutineOccasion,
      teamId,
      now,
      idiom: props.idiom,
      onScroll: setScroll,
      isFocused: props.isFocused,
    };
    switch (widget.content.__typename) {
      case "WidgetTeamScheduleContentDaily":
        return (
          <WidgetTeamScheduleContentDaily
            {...baseProps}
            content={widget.content}
          />
        );
      case "WidgetTeamScheduleContentWeekly":
        return (
          <WidgetTeamScheduleContentWeekly
            {...baseProps}
            content={widget.content}
          />
        );

      case "WidgetTeamScheduleContentMonthly":
        return (
          <WidgetTeamScheduleContentMonthly
            {...baseProps}
            content={widget.content}
          />
        );
      case "WidgetTeamScheduleContentSummary":
        return (
          <WidgetTeamScheduleContentSummary
            {...baseProps}
            content={widget.content}
          />
        );
    }
  }, [
    isDarkTint,
    now,
    onPressRoutineOccasion,
    props.idiom,
    props.interactive,
    props.isFocused,
    props.mode,
    setScroll,
    size,
    tint,
    widget.content,
    widget.id,
    widget.teams,
  ]);

  const setFocus = useSetWidgetFocus(widget.id, props.storecastId);
  const { background } = useWidgetOptions();
  const bgColor = background ?? Color(tint).lightness(98).toString();

  const onPress = useCallback(() => {
    if (props.mode === "FOCUS") {
      setFocus();
    }
  }, [props.mode, setFocus]);

  return (
    <Box
      color="surface"
      style={{
        flex: 1,
        backgroundColor: bgColor,
      }}
    >
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <Pressable
        style={{
          flex: 1,
        }}
        disabled={props.mode !== "FOCUS"}
        onPress={onPress}
      >
        {renderContent()}
      </Pressable>
    </Box>
  );
}
